import { useState, useEffect, useCallback } from "react";

const C = {
  bg0: "#0b1120", bg1: "#111827", bg2: "#1a2236",
  border: "#1e3a5f", text1: "#f1f5f9", text2: "#94a3b8", text3: "#64748b",
  blue: "#3b82f6", green: "#34d399", yellow: "#fbbf24",
  pink: "#f472b6", red: "#ef4444",
};

const S = {
  page: { background: C.bg0, minHeight: "100vh", fontFamily: "'Inter',sans-serif", color: C.text1 },
  header: { padding: "20px 32px", borderBottom: `1px solid ${C.border}`, background: C.bg1, display: "flex", alignItems: "center", gap: 16 },
  title: { fontSize: 20, fontWeight: 800, color: C.text1 },
  back: { background: "transparent", border: `1px solid ${C.border}`, color: C.text2, borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13 },
  body: { padding: "28px 32px", maxWidth: 1200, margin: "0 auto" },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: C.text2, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 14 },
  card: { background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px" },
  grid4: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 },
  statCard: { background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px" },
  statVal: { fontSize: 26, fontWeight: 800, color: C.text1, lineHeight: 1.1 },
  statLbl: { fontSize: 12, color: C.text2, marginTop: 4 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "8px 12px", textAlign: "left", color: C.text3, fontWeight: 600, borderBottom: `1px solid ${C.border}`, fontSize: 12 },
  td: { padding: "10px 12px", borderBottom: `1px solid ${C.border}22` },
  badge: (color) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: color + "22", color }),
  btn: (color = C.blue) => ({ padding: "6px 14px", background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }),
  tabBar: { display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 },
  tab: (active) => ({ padding: "8px 18px", background: "transparent", border: "none", borderBottom: active ? `2px solid ${C.blue}` : "2px solid transparent", color: active ? C.blue : C.text2, cursor: "pointer", fontSize: 13, fontWeight: active ? 700 : 500, marginBottom: -1 }),
};

function StatCard({ value, label, color = C.text1 }) {
  return (
    <div style={S.statCard}>
      <div style={{ ...S.statVal, color }}>{value}</div>
      <div style={S.statLbl}>{label}</div>
    </div>
  );
}

function OverviewTab({ api, token }) {
  const [data, setData] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, db] = await Promise.all([
        fetch(`${api}/api/admin/overview`, { headers: { Authorization: "Bearer " + token } }).then(r => r.json()),
        fetch(`${api}/api/admin/db-stats`, { headers: { Authorization: "Bearer " + token } }).then(r => r.json()),
      ]);
      setData(ov);
      setDbStats(db);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ color: C.text2, padding: 32 }}>Завантаження...</div>;
  if (!data) return null;

  return (
    <div>
      <div style={S.section}>
        <div style={S.sectionTitle}>Система</div>
        <div style={S.grid4}>
          <StatCard value={data.tenants_total} label="Всього тенантів" color={C.blue} />
          <StatCard value={data.tenants_active} label="Активних" color={C.green} />
          <StatCard value={data.orders_total?.toLocaleString()} label="Замовлень в БД" color={C.text1} />
          <StatCard value={data.products_total?.toLocaleString()} label="Товарів в БД" color={C.text1} />
        </div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Синхронізація</div>
        <div style={S.grid3}>
          <StatCard value={data.synclogs_total?.toLocaleString()} label="Всього синк-логів" />
          <StatCard
            value={data.synclogs_running > 0 ? data.synclogs_running : "0"}
            label="Зараз запущено"
            color={data.synclogs_running > 0 ? C.yellow : C.green}
          />
          <StatCard value={data.db_size} label="Розмір БД" color={C.blue} />
        </div>
      </div>

      {dbStats && (
        <div style={S.section}>
          <div style={S.sectionTitle}>Таблиці бази даних</div>
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Таблиця</th>
                  <th style={S.th}>Рядків</th>
                  <th style={S.th}>Розмір</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(dbStats.tables).map(([name, stat]) => (
                  <tr key={name}>
                    <td style={{ ...S.td, fontWeight: 600, color: C.blue }}>{name}</td>
                    <td style={S.td}>{stat.rows?.toLocaleString() ?? "—"}</td>
                    <td style={{ ...S.td, color: C.text2 }}>{stat.size ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TenantsTab({ api, token }) {
  const [tenants, setTenants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetch(`${api}/api/admin/tenants`, { headers: { Authorization: "Bearer " + token } }).then(r => r.json());
      setTenants(data);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    await fetch(`${api}/api/admin/tenants/${id}/toggle`, { method: "POST", headers: { Authorization: "Bearer " + token } });
    load();
  };

  const triggerSync = async (id) => {
    setSyncing(s => ({ ...s, [id]: true }));
    setMsg("");
    try {
      await fetch(`${api}/api/admin/tenants/${id}/sync`, { method: "POST", headers: { Authorization: "Bearer " + token } });
      setMsg(`Синхронізацію для тенанта #${id} запущено`);
    } finally {
      setSyncing(s => ({ ...s, [id]: false }));
    }
  };

  if (loading) return <div style={{ color: C.text2 }}>Завантаження...</div>;

  return (
    <div>
      {msg && <div style={{ background: C.green + "22", border: `1px solid ${C.green}44`, color: C.green, borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>{msg}</div>}
      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>ID</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Магазин</th>
              <th style={S.th}>Статус</th>
              <th style={S.th}>API ключі</th>
              <th style={S.th}>Замовлень</th>
              <th style={S.th}>Товарів</th>
              <th style={S.th}>Остання синх.</th>
              <th style={S.th}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {tenants?.map(t => (
              <tr key={t.id}>
                <td style={{ ...S.td, color: C.text3, fontWeight: 700 }}>#{t.id}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{t.email}</td>
                <td style={{ ...S.td, color: C.text2 }}>{t.shop_name || "—"}</td>
                <td style={S.td}>
                  <span style={S.badge(t.is_active ? C.green : C.red)}>
                    {t.is_active ? "Активний" : "Неактивний"}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={S.badge(t.has_credentials ? C.green : C.yellow)}>
                    {t.has_credentials ? "Є" : "Немає"}
                  </span>
                </td>
                <td style={S.td}>{t.orders_count?.toLocaleString()}</td>
                <td style={S.td}>{t.products_count?.toLocaleString()}</td>
                <td style={{ ...S.td, color: C.text2, fontSize: 12 }}>
                  {t.last_sync ? (
                    <>
                      <div>{new Date(t.last_sync).toLocaleString("uk-UA")}</div>
                      <span style={S.badge(t.last_sync_status === "success" ? C.green : t.last_sync_status === "running" ? C.yellow : C.pink)}>
                        {t.last_sync_status}
                      </span>
                    </>
                  ) : "—"}
                </td>
                <td style={S.td}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {t.has_credentials && (
                      <button
                        style={S.btn(C.blue)}
                        disabled={syncing[t.id]}
                        onClick={() => triggerSync(t.id)}
                      >
                        {syncing[t.id] ? "..." : "Синх"}
                      </button>
                    )}
                    {t.id !== 1 && (
                      <button
                        style={S.btn(t.is_active ? C.red : C.green)}
                        onClick={() => toggle(t.id)}
                      >
                        {t.is_active ? "Вимкнути" : "Увімкнути"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SyncLogsTab({ api, token }) {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staleMsg, setStaleMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetch(`${api}/api/admin/sync-logs?limit=100`, { headers: { Authorization: "Bearer " + token } }).then(r => r.json());
      setLogs(data);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => { load(); }, [load]);

  const cleanStale = async () => {
    const res = await fetch(`${api}/api/admin/sync-logs/stale`, { method: "DELETE", headers: { Authorization: "Bearer " + token } }).then(r => r.json());
    setStaleMsg(`Виправлено зависших записів: ${res.fixed}`);
    load();
  };

  const staleCount = logs?.filter(l => l.status === "running").length ?? 0;

  if (loading) return <div style={{ color: C.text2 }}>Завантаження...</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        {staleCount > 0 && (
          <div style={{ background: C.yellow + "22", border: `1px solid ${C.yellow}44`, color: C.yellow, borderRadius: 8, padding: "8px 14px", fontSize: 13 }}>
            {staleCount} зависших "running" записів
          </div>
        )}
        <button style={S.btn(C.red)} onClick={cleanStale}>Очистити зависші "running"</button>
        {staleMsg && <span style={{ color: C.green, fontSize: 13 }}>{staleMsg}</span>}
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>ID</th>
              <th style={S.th}>Тенант</th>
              <th style={S.th}>Статус</th>
              <th style={S.th}>Початок</th>
              <th style={S.th}>Кінець</th>
              <th style={S.th}>Синхронізовано</th>
              <th style={S.th}>Всього</th>
              <th style={S.th}>Помилка</th>
            </tr>
          </thead>
          <tbody>
            {logs?.map(l => (
              <tr key={l.id}>
                <td style={{ ...S.td, color: C.text3 }}>{l.id}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>#{l.tenant_id}</td>
                <td style={S.td}>
                  <span style={S.badge(
                    l.status === "success" ? C.green :
                    l.status === "running" ? C.yellow :
                    C.red
                  )}>
                    {l.status}
                  </span>
                </td>
                <td style={{ ...S.td, color: C.text2, fontSize: 12 }}>
                  {l.started_at ? new Date(l.started_at).toLocaleString("uk-UA") : "—"}
                </td>
                <td style={{ ...S.td, color: C.text2, fontSize: 12 }}>
                  {l.finished_at ? new Date(l.finished_at).toLocaleString("uk-UA") : "—"}
                </td>
                <td style={S.td}>{l.orders_synced?.toLocaleString() ?? "—"}</td>
                <td style={{ ...S.td, color: C.text3 }}>{l.total_orders?.toLocaleString() ?? "—"}</td>
                <td style={{ ...S.td, color: C.pink, fontSize: 12, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {l.error || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminPage({ apiBaseUrl, token, onBack }) {
  const api = apiBaseUrl || "";
  const [tab, setTab] = useState("overview");

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.back} onClick={onBack}>← Дашборд</button>
        <div style={S.title}>⚙ Адмін-панель</div>
      </div>
      <div style={S.body}>
        <div style={S.tabBar}>
          {[["overview", "Огляд"], ["tenants", "Тенанти"], ["synclogs", "Синк-логи"]].map(([id, label]) => (
            <button key={id} style={S.tab(tab === id)} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
        {tab === "overview" && <OverviewTab api={api} token={token} />}
        {tab === "tenants" && <TenantsTab api={api} token={token} />}
        {tab === "synclogs" && <SyncLogsTab api={api} token={token} />}
      </div>
    </div>
  );
}
